import { useTheme } from '@emotion/react'
import { Product, useFeature } from '@news-mono/web-common'
import React from 'react'
import { getLegacy7NewsDcrMeta } from '../../tracking'
import {
    get7NewsDcrMeta,
    getSectionFromPath,
} from '../../tracking/nielsen/7news-meta'
import { getWANDcrMeta } from '../../tracking/nielsen/wan-meta'
import { AmpAnalytics } from '../analytics'

interface NielsenMeta {
    apid: string
    apv: '1.0'
    apn?: string
    section: string
    segA?: string
    segB?: string
    segC?: string
    subbrand?: string
}

export interface AmpNielsenProps {
    ampApid: string
    apn: string
    location: { pathname: string }
    siteName: string
    product: Product
}

export function generateNielsenMeta(props: {
    ampApid: string
    apn: string
    siteName: string
    pathname: string
    product: Product
    nielsenSubBrandEnabled: boolean
}): NielsenMeta {
    const { apn, siteName, product, pathname, nielsenSubBrandEnabled } = props
    let { ampApid } = props

    const getDcrMeta =
        product === Product.SevenNews
            ? nielsenSubBrandEnabled
                ? get7NewsDcrMeta
                : getLegacy7NewsDcrMeta
            : getWANDcrMeta

    const { section, segA, segB, segC, subbrand } = getDcrMeta(
        siteName,
        pathname,
        'google-amp',
    )

    // generate the right apid based on section
    if (product === Product.SevenNews && nielsenSubBrandEnabled) {
        switch (getSectionFromPath(pathname)) {
            case 'entertainment':
                ampApid = `34435821-0C24-48F7-AF34-E8DF804954E4`
                break
            case 'sport':
                ampApid = `0B1EEDA2-4583-4C09-B28F-BC1BEB431E14`
                break
            case 'lifestyle':
                ampApid = `2FA263AE-C9F4-40ED-8C57-978E53D80834`
                break
            case 'technology':
                ampApid = `E387C8B1-D9CC-4261-AEFF-B0EE381E4948`
                break
            case 'travel':
                ampApid = `6B84A6FB-7B16-455F-B17B-DB60B26EFFE0`
                break
            case 'finance':
                ampApid = `803C636A-F5D3-4892-9E8E-E39C2F27D37B`
                break
            default:
                break
        }
    }

    return {
        apid: ampApid,
        apv: '1.0',
        apn,
        section,
        ...(segA && { segA }),
        ...(segB && { segB }),
        ...(segC && { segC }),
        ...(subbrand && { subbrand }),
    }
}

export const AmpNielsen: React.FC<AmpNielsenProps> = (props) => {
    const { product, location, ampApid, apn, siteName } = props
    const nielsenEventsEnabled = false
    const nielsenSubBrandEnabled = useFeature('nielsen-subbrand')
    const { section } = useTheme()
    const isRegionalSite = product === Product.TheWest && section !== 'default'

    if (!nielsenEventsEnabled || isRegionalSite) {
        return null
    }

    return (
        <AmpAnalytics type="nielsen">
            <script
                type="application/json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        vars: generateNielsenMeta({
                            ampApid,
                            apn,
                            siteName,
                            pathname: location.pathname,
                            product,
                            nielsenSubBrandEnabled,
                        }),
                    }),
                }}
            />
        </AmpAnalytics>
    )
}
